import {ReactNode} from "react"
import {CircularProgress, Stack, Typography} from "@mui/joy"
import {BrainCircuit, Check, TriangleAlert, Users} from "lucide-react"
import {useTranslation} from "react-i18next"
import {AnalyzeProgress as AnalyzeProgressState, AnalyzeStep} from "../context/AnalyzeContext"

export type AnalyzeProgressProps = {
    progress: AnalyzeProgressState
    /** Rendered next to the label while the analysis is still running */
    onCancel?: () => void
}

/** Each stage carries its own icon, so the ring says what is happening without reading the label */
const STEP_ICONS: { [step in AnalyzeStep]?: ReactNode } = {
    matching_teams: <Users size={20}/>,
    fetching_predictions: <BrainCircuit size={20}/>,
    done: <Check size={20}/>,
    error: <TriangleAlert size={20}/>
}

const STEP_COLORS: { [step in AnalyzeStep]?: "primary" | "success" | "danger" } = {
    matching_teams: "primary",
    fetching_predictions: "primary",
    done: "success",
    error: "danger"
}

const RUNNING_STEPS: AnalyzeStep[] = ["matching_teams", "fetching_predictions"]

const AnalyzeProgress = ({progress, onCancel}: AnalyzeProgressProps) => {
    const {t} = useTranslation()
    const running = RUNNING_STEPS.includes(progress.step)
    const color = STEP_COLORS[progress.step] || "primary"

    return <Stack direction={"row"} alignItems={"center"} gap={2}>
        <CircularProgress determinate={!running}
                          value={progress.percent}
                          color={color}
                          size={"lg"}
                          sx={{"--CircularProgress-trackThickness": "4px", "--CircularProgress-progressThickness": "4px"}}>
            <Stack alignItems={"center"} justifyContent={"center"} sx={{color: `${color}.400`}}>
                {STEP_ICONS[progress.step]}
            </Stack>
        </CircularProgress>

        <Stack gap={0.25} flex={1}>
            <Typography level={"title-sm"}>{progress.label}</Typography>
            {running && <Typography level={"body-xs"} textColor={"text.tertiary"}>
                {t("crossingData")}
            </Typography>}
        </Stack>

        {running && onCancel && <Typography level={"body-xs"} onClick={onCancel}
                                            sx={{cursor: "pointer", "&:hover": {color: "white"}}}>
            {t("cancelAnalysis")}
        </Typography>}
    </Stack>
}

export default AnalyzeProgress
